/**
 * Reddit Pixel (id a2_jhrai43bxred) — conversion signal for Reddit Ads.
 *
 * Loads ONLY in production builds AND only for non-internal browsers (same
 * localStorage flag GA4's internal-traffic exclusion uses), so founder/QA
 * walks never train Reddit's optimizer. Events queue before the script
 * arrives, matching Reddit's official snippet semantics.
 *
 * Standard events we send: PageVisit (on init), ViewContent (diagnostic
 * started), Lead (diagnostic email captured), Purchase (payment success),
 * SignUp (trial signup complete). Plus a custom 'DiagnosticStart' event at the
 * same start trigger so Reddit reports the top-of-funnel entry on the SAME
 * denominator as GA4's `diagnostic_started` (cost-per-start), not just clicks.
 */
import { isInternalTraffic } from './internalTraffic';
import { isPrerendering } from './prerender';

const PIXEL_ID = 'a2_jhrai43bxred';

type RdtFn = ((...args: unknown[]) => void) & { callQueue?: unknown[][]; sendEvent?: unknown };

function rdt(): RdtFn | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as { rdt?: RdtFn };
  if (!w.rdt) {
    const queued: RdtFn = ((...args: unknown[]) => {
      if (queued.sendEvent) {
        (queued.sendEvent as (...a: unknown[]) => void).apply(queued, args);
      } else {
        queued.callQueue?.push(args);
      }
    }) as RdtFn;
    queued.callQueue = [];
    w.rdt = queued;
  }
  return w.rdt;
}

let initialized = false;

/** True when the pixel should run at all: prod build, real visitor, not the prerenderer. */
function enabled(): boolean {
  return import.meta.env.PROD && !isPrerendering() && !isInternalTraffic();
}

/** Call once at app boot (after internal-traffic flag handling). */
export function initRedditPixel(): void {
  if (initialized || !enabled()) return;
  const r = rdt();
  if (!r) return;
  initialized = true;
  r('init', PIXEL_ID);
  r('track', 'PageVisit');
  const s = document.createElement('script');
  s.src = 'https://www.redditstatic.com/ads/pixel.js';
  s.async = true;
  document.head.appendChild(s);
}

/**
 * Fire a Reddit standard event (e.g. 'Lead', 'SignUp', 'Purchase'). Optional
 * metadata (value/currency/transactionId/itemCount) rides along for 'Purchase'
 * so Reddit can report revenue, not just conversion counts. No-op for internal
 * traffic or non-prod builds.
 */
export function trackRedditEvent(
  event: 'Lead' | 'SignUp' | 'Purchase' | 'ViewContent',
  metadata?: { value?: number; currency?: string; transactionId?: string; itemCount?: number },
): void {
  if (!initialized || !enabled()) return;
  rdt()?.('track', event, metadata);
}

/**
 * Fire a Reddit CUSTOM conversion event (Reddit's standard set has no "start",
 * so diagnostic-start rides here as 'DiagnosticStart'). Reddit's snippet takes
 * the custom name via the `customEventName` field on a 'Custom' track call.
 * Define a matching custom conversion in Reddit Ads to optimize toward it.
 * No-op for internal traffic or non-prod builds.
 */
export function trackRedditCustomEvent(
  name: string,
  metadata?: Record<string, unknown>,
): void {
  if (!initialized || !enabled()) return;
  rdt()?.('track', 'Custom', { customEventName: name, ...metadata });
}
